import { Component, inject, signal } from '@angular/core';

import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import * as fromSharedUi from '@sandbox-platform/shared-ui';
import { AuthStore, type AuthStoreType } from '../../../store/auth.store';
import { ChangePasswordDTO } from '../../../types/auth.types';

@Component({
  standalone: true,
  selector: 'app-force-password-reset',
  imports: [
    ReactiveFormsModule,
    fromSharedUi.TextInputComponent,
    fromSharedUi.SubmitButtonComponent,
    fromSharedUi.ErrorAlertComponent,
    fromSharedUi.SuccessAlertComponent
],
  templateUrl: './force-password-reset.component.html',
})
export class ForcePasswordResetComponent {
  protected readonly store = inject(AuthStore) as AuthStoreType;
  private readonly fb = inject(FormBuilder);
  private readonly router = inject(Router);

  constructor() {
    this.store.clearError();
  }

  readonly form = this.fb.group({
    current_password: ['', [Validators.required]],
    new_password: ['', [Validators.required, Validators.minLength(8)]],
    confirm_password: ['', [Validators.required]],
  });

  readonly formSubmitted = signal(false);
  readonly successMessage = signal<string | null>(null);

  get userEmail(): string {
    return this.store.user()?.email ?? '';
  }

  passwordsMismatch(): boolean {
    const newPassword = this.form.controls.new_password.value;
    const confirmPassword = this.form.controls.confirm_password.value;
    return (
      !!newPassword && !!confirmPassword && newPassword !== confirmPassword
    );
  }

  newMatchesCurrent(): boolean {
    const currentPassword = this.form.controls.current_password.value;
    const newPassword = this.form.controls.new_password.value;
    return (
      !!currentPassword && !!newPassword && currentPassword === newPassword
    );
  }

  async submit(): Promise<void> {
    this.formSubmitted.set(true);

    if (
      this.form.invalid ||
      this.passwordsMismatch() ||
      this.newMatchesCurrent()
    ) {
      return;
    }

    const payload = this.form.value as ChangePasswordDTO;

    const success = await this.store.changePassword(payload);
    if (success) {
      this.form.reset();
      this.formSubmitted.set(false);
      this.successMessage.set(
        'Password updated successfully. Please sign in with your new password.'
      );
      await this.store.logout();
      setTimeout(() => {
        this.successMessage.set(null);
        this.router.navigate(['/login']);
      }, 1500);
    }
  }
}
